// ============================================================
// KioskQrPage — 月度 QR 列印頁
// ============================================================
// 老闆用：
//   - 顯示當月（或選擇月份）大 QR
//   - 列印 → 護貝 → 貼牆
//   - 重新產生（萬一外洩）
//   - 預先產生下個月（月底用）
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { taipeiThisMonth, shiftMonth } from '../lib/datetime';

interface QrData {
  token: string;
  month: string;
  qrDataUrl: string;
}

export function KioskQrPage() {
  const { tenant } = useAuthStore();
  // 用台北月份。原本是 UTC 月份：台北每月 1 號 00:00–07:59 會開在
  // 上個月，老闆若在那時段列印就會貼出一張已失效的 QR。
  const [month, setMonth] = useState(taipeiThisMonth);
  const [data, setData] = useState<QrData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQr = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<QrData>(`/monthly-qr?month=${month}`);
      setData(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchQr(); }, [fetchQr]);

  const handlePrint = () => {
    window.print();
  };

  const handleRegenerate = async () => {
    if (!confirm('確定重新產生 QR？\n\n舊的 QR 會立即失效，請務必重新列印貼上。')) return;
    try {
      const r = await api.post<QrData>('/monthly-qr/regenerate', { month });
      setData(r);
      alert('已重新產生新 QR，請重新列印');
    } catch (err) {
      alert('失敗：' + (err as Error).message);
    }
  };

  const navigateMonth = (delta: number) => setMonth(m => shiftMonth(m, delta));

  const [yyyy, mm] = month.split('-');
  const monthLabel = `${yyyy} 年 ${parseInt(mm)} 月`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* 螢幕版頭部（列印時隱藏） */}
      <div className="print:hidden">
        <h1 className="text-xl font-bold text-morandi-800">打卡 QR Code</h1>
        <p className="text-xs text-morandi-400 mt-1">
          每月一張，護貝後貼在收銀台旁牆面 · 員工掃描即可打卡（掃到此 QR 即視為人在店內，不需定位）
        </p>
      </div>

      {/* 控制列 — 列印時隱藏 */}
      <div className="print:hidden flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigateMonth(-1)} className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center">‹</button>
          <span className="text-base font-semibold text-morandi-700 min-w-[120px] text-center">{monthLabel}</span>
          <button onClick={() => navigateMonth(1)} className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center">›</button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRegenerate}
            className="px-3 py-2 text-xs bg-white border border-rose-300 text-rose-500 hover:bg-rose-50 rounded-btn"
          >
            重新產生
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn"
          >
            🖨 列印
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-morandi-400">載入中...</div>
      ) : error ? (
        <div className="text-center py-20 text-rose-500">{error}</div>
      ) : data ? (
        // 列印區（A4 直式）
        <div className="bg-white rounded-2xl shadow-card p-8 print:shadow-none print:rounded-none print:p-0">
          <div className="text-center print:py-12">
            {/* 店鋪 logo */}
            <div className="inline-flex items-center justify-center w-16 h-16 bg-earth-200 rounded-2xl mb-3">
              <span className="text-2xl text-earth-600 font-bold">天</span>
            </div>
            <h2 className="text-3xl font-bold text-morandi-800 mb-1">
              {tenant?.name ?? '天水豆腐店'}
            </h2>
            <div className="text-base text-morandi-500 mb-2">員工打卡 QR Code</div>
            <div className="text-2xl font-bold text-rose-500 mb-6 tracking-widest">
              {monthLabel}
            </div>

            {/* QR Code */}
            <div className="inline-block bg-white p-4 border-2 border-morandi-200 rounded-xl">
              <img
                src={data.qrDataUrl}
                alt="QR Code"
                className="w-72 h-72 print:w-96 print:h-96"
              />
            </div>

            <div className="mt-6 text-sm text-morandi-500 space-y-1">
              <div>📱 開啟天水 App → 點立即打卡 → 掃描此 QR</div>
              <div>📍 掃描成功即完成，不需開啟定位</div>
              <div className="text-xs text-morandi-400 mt-3">
                此 QR 將於下個月失效，請勿撕除或拍照外傳
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 列印用 CSS */}
      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: A4 portrait; margin: 1cm; }
        }
      `}</style>
    </div>
  );
}
